import { config } from './config'
import { session } from './session.svelte'
import type {
  Account,
  CheckoutSession,
  EmailPreferenceChange,
  EmailPreferences,
  MarketingConsent,
  MarketingPreference,
  PasswordResetTarget,
  Session,
  Subscription,
} from './types'

/*
 * The HTTP client for both context APIs.
 *
 * Two base URLs, not one: `api.account.birdjournal.com` and `api.billing.birdjournal.com` are separate
 * bounded contexts that happen to be rendered on the same pages (design `platform-architecture` §6a.2b).
 * They are reached through the same function because the credential and the error handling are shared;
 * they are named separately at every call site because confusing them should be a compile error.
 */

/** An error the caller is expected to render. `status` is what decides the copy, never the message. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string | null,
  ) {
    super(detail ?? `HTTP ${status}`)
    this.name = 'ApiError'
  }
}

/** Raised when the browser could not reach the API at all — distinct from any answer it gave. */
export class OfflineError extends Error {
  constructor() {
    super('Could not reach Bird Journal')
    this.name = 'OfflineError'
  }
}

/**
 * Set by the router so a 401 anywhere lands on the sign-in page rather than leaving a half-rendered
 * account page behind an error. Injected rather than imported to keep the router out of this module's
 * dependencies — they would otherwise import each other.
 */
let onUnauthorised: () => void = () => {}
export const handleUnauthorisedWith = (handler: () => void) => {
  onUnauthorised = handler
}

type Options = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  /** Signed-out calls — sign in, password reset. Sending a stale token to these is meaningless. */
  anonymous?: true
}

async function call<T>(base: string, path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, anonymous } = options

  const headers: Record<string, string> = { accept: 'application/json' }
  if (body !== undefined) headers['content-type'] = 'application/json'
  if (!anonymous && session.token) headers.authorization = `Bearer ${session.token}`

  let response: Response
  try {
    response = await fetch(`${base}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      // No cookies, deliberately: the credential is the bearer header. When §13.6 moves to a cookie this
      // becomes 'include' AND the API has to name an origin rather than allow '*' — the two are one
      // change, and neither works alone.
      credentials: 'omit',
    })
  } catch {
    // A network failure is NOT an ApiError. Reporting "something went wrong" for a dropped connection
    // sends people to support for a problem they can see the cause of themselves.
    throw new OfflineError()
  }

  if (response.status === 401 && !anonymous) {
    session.clear()
    onUnauthorised()
    throw new ApiError(401, null)
  }

  if (!response.ok) {
    // RFC 9457 problem+json. `detail` is for the page to use where it has nothing better to say; every
    // error with its own copy branches on `status` instead, so a wording change in the API cannot
    // silently change what a customer reads here.
    let detail: string | null = null
    try {
      const problem = (await response.json()) as { detail?: string; title?: string }
      detail = problem.detail ?? problem.title ?? null
    } catch {
      detail = null
    }
    throw new ApiError(response.status, detail)
  }

  /*
   * PARSE ONLY WHEN THERE IS SOMETHING TO PARSE. Special-casing 204 alone is not enough and was a real
   * bug: `POST /password-reset-requests` and `POST /account/deletion-requests` both answer **202** with
   * an empty body, so `response.json()` threw a SyntaxError on the success path — and the pages, unable
   * to tell that apart from a fault, told people their request had failed when it had gone through.
   * Caught by an end-to-end journey rather than by a type, because the types said it returned void.
   */
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

const account = <T>(path: string, options?: Options) => call<T>(config().accountApi, path, options)
const billing = <T>(path: string, options?: Options) => call<T>(config().billingApi, path, options)

// ── account context ──────────────────────────────────────────────────────────────────────────

export const api = {
  signIn: (email: string, password: string) =>
    account<Session>('/sessions', { method: 'POST', body: { email, password }, anonymous: true }),

  /**
   * Best-effort: the local session is cleared regardless. A sign-out that appeared to fail because the
   * network dropped, leaving somebody signed in on a shared machine, is the worse outcome by far.
   */
  signOut: () => account<void>('/sessions/current', { method: 'DELETE' }).catch(() => undefined),

  requestPasswordReset: (email: string) =>
    account<void>('/password-reset-requests', { method: 'POST', body: { email }, anonymous: true }),

  passwordResetTarget: (token: string) =>
    account<PasswordResetTarget>(`/password-resets/${encodeURIComponent(token)}`, { anonymous: true }),

  resetPassword: (token: string, password: string) =>
    account<void>(`/password-resets/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: { password },
      anonymous: true,
    }),

  account: () => account<Account>('/account'),

  marketingConsent: () => account<MarketingPreference>('/account/marketing-consent'),

  setMarketingConsent: (consent: MarketingConsent) =>
    account<MarketingPreference>('/account/marketing-consent', { method: 'PUT', body: { consent } }),

  requestDeletion: () => account<void>('/account/deletion-requests', { method: 'POST' }),

  /*
   * Redeemed with the token from an unsubscribe link, NOT with a session — `anonymous` so no stale bearer
   * rides along and a 401 cannot bounce somebody to sign-in from a page they reached out of an email.
   */
  emailPreferences: (token: string) =>
    account<EmailPreferences>(`/email-preferences?t=${encodeURIComponent(token)}`, { anonymous: true }),

  changeEmailPreference: (token: string, change: EmailPreferenceChange) =>
    account<void>(`/email-preferences?t=${encodeURIComponent(token)}`, {
      method: 'POST',
      body: change,
      anonymous: true,
    }),

  // ── billing context ────────────────────────────────────────────────────────────────────────

  subscription: () => billing<Subscription>('/subscription'),

  checkoutSession: (returnPath: string) =>
    billing<CheckoutSession>('/checkout-sessions', { method: 'POST', body: { returnPath } }),

  removePaymentMethod: () => billing<void>('/subscription/payment-method', { method: 'DELETE' }),
}
